import * as EsriLeaflet from 'esri-leaflet';
import * as EsriLeafletVector from 'esri-leaflet-vector';
import type { Map, TileLayer } from 'leaflet';
import { tileLayer } from 'leaflet';
import React, { useEffect } from 'react';
import 'leaflet.vectorgrid';
import vectorTileLayer from 'leaflet-vector-tile-layer';
import proj4 from 'proj4';
import * as Proj4Leaflet from 'proj4leaflet';

import type {
  BackgroundLayerConfig,
  MapConfiguration
} from '@/common/overmind/namespaces/arcgis/config/config.types';
import { useWebMap } from '@/features/execute-calculation-set/components/Calculation/GridElementsCalculationTacticResults/useFeatureDetails';
import { useActions, useAppState } from '@/overmind';

type BaseLayers = Record<BackgroundLayerConfig['id'], TileLayer>;

function getBaseLayers(layerConfigs: BackgroundLayerConfig[]) {
  const backgroundLayers: BaseLayers = {};

  layerConfigs.forEach(layer => {
    if (layer.type === 'tile') {
      backgroundLayers[layer.id] = tileLayer(layer.url, {
        attribution: layer.attribution,
        maxNativeZoom: layer.maxNativeZoom ?? 18,
        maxZoom: 25,
        className: layer.monochrome ? 'monochrome-map' : undefined
      });
    } else if (layer.type === 'wms') {
      backgroundLayers[layer.id] = tileLayer.wms(layer.url, {
        attribution: layer.attribution,
        maxNativeZoom: layer.maxNativeZoom ?? 18,
        layers: layer.layers,
        maxZoom: 25,
        className: layer.monochrome ? 'monochrome-map' : undefined
      });
    }
  });
  return backgroundLayers;
}

function getBaseLayer({
  backgroundLayers,
  defaultBackgroundLayerId
}: MapConfiguration['map']): TileLayer | undefined {
  const baseLayers = getBaseLayers(backgroundLayers);
  if (defaultBackgroundLayerId && baseLayers[defaultBackgroundLayerId]) {
    return baseLayers[defaultBackgroundLayerId];
  } else {
    return Object.values(baseLayers)[0];
  }
}

export function useBaseMapLayer(map: Map | null) {
  const { backgroundMapReady, backgroundMapNotReady } = useActions().map;
  const webMap = useWebMap();

  React.useEffect(() => {
    if (webMap.type === 'READY' && map) {
      const basemap = webMap.webMap.basemap.baseLayers.at(0);
      if (basemap.type === 'tile') {
        const tileLayer = EsriLeaflet.tiledMapLayer({
          url: basemap.url
        });
        tileLayer.addTo(map);
        map.fitBounds(map.getBounds()); // when adding tiled map layer that has non-Mercator projection, we need to trigger fitting to bounds so that tiles are loaded correctly
        backgroundMapReady();
      } else if (basemap.type === 'vector-tile') {
        // vector tile layers and leaflet does not work seamlessly, adding for documentation purposes
        const vectorLayer = EsriLeafletVector.vectorTileLayer(
          basemap.serviceUrl,
          {}
        );
        // styles not working, but projection works
        // const vt2 = L.vectorGrid.protobuf(
        //   "https://vector.services.geodataonline.no/arcgis/rest/services/GeocacheVector/GeocacheGraatone_UTM32/VectorTileServer/tile/{z}/{y}/{x}.pbf"
        // )

        // styles not working, but projection works
        // const vt3 = vectorTileLayer(
        //   "https://vector.services.geodataonline.no/arcgis/rest/services/GeocacheVector/GeocacheGraatone_UTM32/VectorTileServer/tile/{z}/{y}/{x}.pbf"
        // )
        map.fitBounds(map.getBounds()); // when adding tiled map layer that has non-Mercator projection, we need to trigger fitting to bounds so that tiles are loaded correctly
        vectorLayer.addTo(map);
        backgroundMapReady();
      }
    }

    return () => {
      backgroundMapNotReady();
    };
  }, [webMap, map]);

  const config = useAppState().arcgis.config.value;

  // useEffect(() => {
  //   let baseLayer: TileLayer | undefined;
  //   if (config && map) {
  //     baseLayer = getBaseLayer(config.map);
  //     if (baseLayer) {
  //       baseLayer.addTo(map);
  //     }
  //   }

  //   return () => {
  //     if (baseLayer && map && map.hasLayer(baseLayer)) {
  //       map.removeLayer(baseLayer);
  //     }
  //   };
  // }, [config, map]);
}

export const useBasemapProjection = () => {
  const resolutions = [
    21674.7100160867, 10837.35500804335, 5418.677504021675, 2709.3387520108377,
    1354.6693760054188, 677.3346880027094, 338.6673440013547,
    169.33367200067735, 84.66683600033868, 42.33341800016934, 21.16670900008467,
    10.583354500042335, 5.291677250021167, 2.6458386250105836,
    1.3229193125052918, 0.6614596562526459, 0.33072982812632296,
    0.16536491406316148
  ];
  proj4.defs(
    'EPSG:25832',
    '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs'
  );
  const crs = new Proj4Leaflet.CRS(
    'EPSG:25832',
    '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
    {
      resolutions,
      origin: [-2000000.0, 9045984.0]
    }
  );
  const newProjection = {
    crs,
    transform: (coords: any) =>
      proj4(
        'EPSG:4326',
        '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
        coords
      )
  };
  const [projection, setProjection] = React.useState(newProjection);
  return projection;
};
